import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import { Alert, Badge, Button, Table } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronRight } from "lucide-react";
import CreateProjectPageComponentModal from "../project-page-component/CreateProjectPageComponentModal";

/**
 * Reusable section component for displaying Project Page Components
 * @param {string} projectPageId - The project page ID to fetch components for
 */
const ProjectPageComponentsSection = ({ projectPageId, projectPageName }) => {
  const { projectPageComponent } = useServices();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [list, setList] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  async function load() {
    if (!projectPageId) return;
    setLoading(true);
    setError("");
    try {
      const results =
        await projectPageComponent.fetchProjectPageComponentsByProjectPageId(
          projectPageId,
        );
      setList(Array.isArray(results) ? results : []);
    } catch (e) {
      setError(e?.message || "Failed to load page components");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPageId]);

  const handleAddSuccess = () => {
    setShowAddModal(false);
    load(); // Reload the list after successful creation
  };

  return (
    <>
      <Badge.Ribbon
        placement="start"
        style={{ display: loading === false ? "block" : "none" }}
        text={`#${list.length}`}
        color="blue"
      >
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between p-6">
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="flex-1 flex items-start flex-col text-left"
            >
              <div className="text-lg font-semibold text-gray-900">
                Page Components
              </div>
              <div className="text-sm text-gray-600">
                Components associated with this project page
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button
                type="primary"
                onClick={() => setShowAddModal(true)}
                size="small"
                icon={<PlusOutlined />}
              >
                Add
              </Button>
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                className="p-1"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {isExpanded && (
            <div className="border-t border-gray-200 p-4">
              {error && (
                <div className="mb-2">
                  <Alert type="error" showIcon message={error} />
                </div>
              )}
              <Table
                loading={loading}
                dataSource={list}
                rowKey="Id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Name",
                    dataIndex: "Name",
                    key: "Name",
                    ellipsis: true,
                    render: (text, rec) => (
                      <Link
                        to={{ pathname: `/ppc/${rec.Id}` }}
                        state={{ crumbLabel: rec.Name }}
                        className="text-primary-600"
                      >
                        {text || rec.Id}
                      </Link>
                    ),
                  },
                  {
                    title: "Component Type",
                    dataIndex: ["RecordType", "Name"],
                    key: "RecordType",
                    width: "40%",
                    ellipsis: true,
                    render: (recordType) => {
                      const recordTypeMap = {
                        1: "2 Column 50-50",
                        2: "2 Column Image Left 50-50",
                        3: "2 Column Image Right 50-50",
                        4: "2 Column Image Left 33-66",
                        5: "2 Column Image Right 33-66",
                        6: "3 Column 33-33-33",
                        7: "Headline only",
                        8: "Text Only Full Page",
                        9: "Standalone Button",
                        10: "Image Only",
                        11: "Microsoft Project Page",
                        12: "Splash Page",
                      };
                      return recordTypeMap[recordType] || recordType || "-";
                    },
                  },
                ]}
              />
            </div>
          )}
        </div>
      </Badge.Ribbon>
      <CreateProjectPageComponentModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        initialValues={{
          projectPageId: projectPageId,
          projectPageName: projectPageName,
        }}
      />
    </>
  );
};

export default ProjectPageComponentsSection;
